import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import OTPLogin from "../components/OTPLogin";
import WelcomeModalV2 from "../components/WelcomeModalV2";
import ReviewAndEditV2 from "../components/client/ReviewAndEdit/ReviewAndEditV2";
import FinalReviewV2 from "../components/client/FinalReviewV2";
import PendingDataV2 from "../components/client/PendingData/PendingDataV2";
import DocumentUploadV2 from "../components/client/DocumentUploadV2";
import SSEToastContainer from "../components/ui/SSEToast";
import useSSE from "../hooks/useSSE";
import clientAxiosInstance from "../api/clientAxiosInstance";

const ClientPortal = () => {
  const { household_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [householdData, setHouseholdData] = useState(null);
  const [clientToken, setClientToken] = useState(null);
  const [isNewOTPLogin, setIsNewOTPLogin] = useState(false);
  const [sseToasts, setSseToasts] = useState([]);
  const [docsStatusSSE, setDocsStatusSSE] = useState(null);
  const hasFetchedRef = useRef(false);

  const isFinalReviewRoute = location.pathname.includes("/final-review");
  const isFullReviewRoute = location.pathname.includes("/full-review");
  const isDocumentUploadV2Route = location.pathname.includes("/document-upload");
  const isPendingDataRoute = location.pathname.includes("/pending-data");

  // Check if user is on a specific route (should not be redirected)
  const isOnSpecificRoute =
    isFinalReviewRoute ||
    isFullReviewRoute ||
    isDocumentUploadV2Route ||
    isPendingDataRoute;

  const fetchHouseholdData = useCallback(
    async (token, forceRefresh = false) => {
      if (hasFetchedRef.current && !forceRefresh) {
        return;
      }

      hasFetchedRef.current = true;

      try {
        setLoading(true);
        setError("");
        const response = await clientAxiosInstance.get(
          `/client/household/${household_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setHouseholdData(response.data);
      } catch (error) {
        console.error("Error fetching household data:", error);
        setError(
          error.response?.data?.detail ||
            "Failed to load household data. Please try again.",
        );
        // If token is invalid, clear it
        if (error.response?.status === 401) {
          localStorage.removeItem(`client_token_${household_id}`);
          localStorage.removeItem(`client_token_expiry_${household_id}`);
          setIsAuthenticated(false);
          setClientToken(null);
          hasFetchedRef.current = false; // Reset on auth failure
        }
      } finally {
        setLoading(false);
      }
    },
    [household_id],
  );

  // Silent refresh - fetches data without showing loading spinner
  const silentRefreshHouseholdData = useCallback(async () => {
    if (!clientToken) return;
    try {
      const response = await clientAxiosInstance.get(
        `/client/household/${household_id}`,
        {
          headers: {
            Authorization: `Bearer ${clientToken}`,
          },
        },
      );
      setHouseholdData(response.data);
    } catch (error) {
      console.error("Failed to silently refresh household data:", error);
    }
  }, [household_id, clientToken]);

  // Refresh household data function - supports both direct data update and refetch
  const refreshHouseholdData = useCallback(
    (updatedData) => {
      if (updatedData) {
        // If data is provided, update state directly (no refresh)
        setHouseholdData(updatedData);
      } else if (clientToken) {
        // If no data provided, do a silent refresh (no loading spinner)
        silentRefreshHouseholdData();
      }
    },
    [clientToken, silentRefreshHouseholdData],
  );

  const dismissToast = useCallback((toastId) => {
    setSseToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  // SSE: refresh household data and show toast when document processing events arrive
  const handleSSEEvent = useCallback(
    (event) => {
      if (event.notification_type === "DOCUMENT_PROCESSED") {
        silentRefreshHouseholdData();

        // Show toast notification
        const isSuccess = event.document_status === "completed";
        setSseToasts((prev) => [
          ...prev,
          {
            id: event.id || `toast-${Date.now()}`,
            title: isSuccess
              ? "Document processed successfully"
              : "Document processing failed",
            fileName: event.file_name || "Unknown document",
            status: event.document_status,
            duration: 8000,
          },
        ]);
      } else if (event.notification_type === "document_batch_status") {
        setDocsStatusSSE(event);
      } else if (
        event.notification_type === "TRANSCRIPT_PROCESSED" ||
        event.notification_type === "AUDIO_PROCESSED"
      ) {
        silentRefreshHouseholdData();
      }
    },
    [silentRefreshHouseholdData],
  );

  useSSE({
    clientToken,
    enabled: isAuthenticated && !!clientToken,
    onEvent: handleSSEEvent,
  });

  // Show "household does not exist" for invalid statuses
  useEffect(() => {
    if (!householdData || !isAuthenticated) return;

    const invalidStatuses = [
      "created",
      "general_info_completed",
      "household_setup_initiated",
      "active",
      "completed",
      "inactive",
    ];
    if (invalidStatuses.includes(householdData.status)) {
      setError("This household does not exist or is not available.");
    }
  }, [householdData, isAuthenticated]);

  // Derive welcome modal visibility: show when we have data and user is on the base route
  const showWelcomeModal = householdData && !isOnSpecificRoute;

  // Check if client is already authenticated
  useEffect(() => {
    const storedToken = localStorage.getItem(`client_token_${household_id}`);
    const tokenExpiry = localStorage.getItem(
      `client_token_expiry_${household_id}`,
    );

    if (
      storedToken &&
      tokenExpiry &&
      new Date().getTime() < parseInt(tokenExpiry)
    ) {
      setClientToken(storedToken);
      setIsAuthenticated(true);
      fetchHouseholdData(storedToken);
    }

    // Reset flags when household_id changes (cleanup)
    return () => {
      hasFetchedRef.current = false;
    };
  }, [household_id]); // Removed fetchHouseholdData from dependencies

  const handleAuthSuccess = (token, expiresIn) => {
    setClientToken(token);
    setIsAuthenticated(true);
    setIsNewOTPLogin(true); // Mark this as a new OTP login

    // Store token with expiry
    const expiryTime = new Date().getTime() + expiresIn * 1000;
    localStorage.setItem(`client_token_${household_id}`, token);
    localStorage.setItem(
      `client_token_expiry_${household_id}`,
      expiryTime.toString(),
    );

    // Reset flags before calling fetch (for OTP login scenario)
    hasFetchedRef.current = false;
    fetchHouseholdData(token);

    // Note: Navigation is handled by the status-based routing useEffect after data is loaded
    // If user is on /final-review route directly, they won't be redirected (isOnSpecificRoute check)
  };

  const handleLogout = () => {
    localStorage.removeItem(`client_token_${household_id}`);
    localStorage.removeItem(`client_token_expiry_${household_id}`);
    setIsAuthenticated(false);
    setClientToken(null);
    setHouseholdData(null);
    setIsNewOTPLogin(false);
  };

  const handleGetStarted = () => {
    navigate(`/household/${household_id}/document-upload`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600">
            Loading your information
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <OTPLogin
        household_id={household_id}
        onAuthSuccess={handleAuthSuccess}
        error={error}
        setError={setError}
      />
    );
  }

  // Show error message for invalid statuses
  if (householdData) {
    const invalidStatuses = [
      "created",
      "general_info_completed",
      "household_setup_initiated",
      "active",
      "completed",
      "inactive",
    ];
    if (invalidStatuses.includes(householdData.status) && !isOnSpecificRoute) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto">
              <svg
                className="mx-auto h-12 w-12 text-red-500 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Household Not Available
              </h2>
              <p className="text-gray-600 mb-4">
                This household does not exist or is not available at this time.
              </p>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Toast overlay — shared across all authenticated client screens
  const toastOverlay = (
    <SSEToastContainer toasts={sseToasts} onDismiss={dismissToast} />
  );

  // Show final review only when status is shared_with_client_for_final_review
  if (isFinalReviewRoute) {
    if (householdData?.status !== "shared_with_client_for_final_review") {
      return <Navigate to={`/household/${household_id}`} replace />;
    }
    return (
      <>
        {toastOverlay}
        <FinalReviewV2
          householdData={householdData}
          household_id={household_id}
          clientToken={clientToken}
          onLogout={handleLogout}
          onDataUpdate={setHouseholdData}
        />
      </>
    );
  }

  if (isFullReviewRoute) {
    return (
      <>
        {toastOverlay}
        <ReviewAndEditV2
          householdData={householdData}
          household_id={household_id}
          clientToken={clientToken}
          onLogout={handleLogout}
          onDataUpdate={refreshHouseholdData}
        />
      </>
    );
  }

  if (isPendingDataRoute) {
    return (
      <>
        {toastOverlay}
        <PendingDataV2
          householdData={householdData}
          household_id={household_id}
          clientToken={clientToken}
          onDataUpdate={refreshHouseholdData}
          onLogout={handleLogout}
        />
      </>
    );
  }

  // Show new document upload V2 page
  if (isDocumentUploadV2Route) {
    return (
      <>
        {toastOverlay}
        <DocumentUploadV2
          householdData={householdData}
          household_id={household_id}
          clientToken={clientToken}
          onContinue={() => navigate(`/household/${household_id}/pending-data`)}
          onDataUpdate={refreshHouseholdData}
          docsStatusSSE={docsStatusSSE}
        />
      </>
    );
  }

  const fallbackPath = (() => {
    const status = householdData?.status;
    if (status === "shared_with_client_for_final_review") {
      return `/household/${household_id}/final-review`;
    }
    if (
      status === "shared_with_client_onboarding_done" ||
      status === "pending_advisor_review" ||
      status === "advisor_final_review" ||
      status === "pending_form_download"
    ) {
      return `/household/${household_id}/full-review`;
    }
    return `/household/${household_id}/document-upload`;
  })();

  return (
    <>
      {toastOverlay}
      {showWelcomeModal && householdData ? (
        <WelcomeModalV2
          householdData={householdData}
          onGetStarted={handleGetStarted}
        />
      ) : (
        <Navigate to={fallbackPath} replace />
      )}
    </>
  );
};

export default ClientPortal;
